import { Box, FormGroup } from "@mui/material";
import { Button, Card, Checkbox, Grid, Modal, ModalTitle, Typography, designTokens } from "@platform-ui/design-system";
import React, { useState, useEffect, useRef, useCallback } from "react"
import { DataGrid, GridRowSelectionModel, GridToolbar } from '@mui/x-data-grid';
import { BillingModelMetaData } from "../Constants";
import { Destination } from "@prequel/react";
import { DiscardChangesModal } from "./DiscardChangesModal";

interface DisplayModelsProps {
    rows: BillingModelMetaData[];
    futureObjectsSyncOn: boolean;
    setDestinationField: (key: keyof Destination, value: string | string[] | boolean | undefined) => void;
    onSubmitModelsProvision: (selectedModels: string[]) => void;
}

export const DisplayModels = ({ rows, futureObjectsSyncOn, setDestinationField, onSubmitModelsProvision }: DisplayModelsProps) => {
    const getInitialSelection = (): GridRowSelectionModel => {
        if (futureObjectsSyncOn) {
            return rows.map((row) => row.id);
        }
        const provisionedNames = new Set(rows.map(r => r.objectName));
        return rows.filter(m => provisionedNames.has(m.objectName)).map(m => m.id);
    };

    // useRef to store the initial selection and all current and future models selected
    const savedRowSelectionModel = useRef<GridRowSelectionModel>(getInitialSelection());
    const savedAllCurrentAndFutureModelsSelected = useRef<boolean>(futureObjectsSyncOn);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(getInitialSelection());
    const [allCurrentAndFutureModelsSelected, setAllCurrentAndFutureModelsSelected] = useState(futureObjectsSyncOn);
    const [modalOpen, setModalOpen] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 10,
        page: 0,
    });

    useEffect(() => {
        if (futureObjectsSyncOn) {
            savedAllCurrentAndFutureModelsSelected.current = true;
            savedRowSelectionModel.current = rows.map((row) => row.id);
        } else {
            savedAllCurrentAndFutureModelsSelected.current = false;
            const provisionedNames = new Set(rows.map(r => r.objectName));
            savedRowSelectionModel.current = rows.filter(m => provisionedNames.has(m.objectName)).map(m => m.id);
        }
    }, [futureObjectsSyncOn, rows]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useCallback(() => {
        if (allCurrentAndFutureModelsSelected !== savedAllCurrentAndFutureModelsSelected.current) {
            return true;
        }
        const currentSelection = new Set(rowSelectionModel);
        const savedSelection = new Set(savedRowSelectionModel.current);
        if (currentSelection.size !== savedSelection.size) {
            return true;
        }
        for (const id of Array.from(currentSelection)) {
            if (!savedSelection.has(id)) {
                return true;
            }
        }
        return false;
    }, [allCurrentAndFutureModelsSelected, rowSelectionModel]);

    // Display discard changes modal when trying to refresh/navigate away
    useEffect(() => {
        if (!modalOpen) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges()) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [modalOpen, hasUnsavedChanges]);

    const resetToSavedState = () => {
        setAllCurrentAndFutureModelsSelected(savedAllCurrentAndFutureModelsSelected.current);
        setRowSelectionModel([...savedRowSelectionModel.current]);
    };

    const onClose = () => {
        if (hasUnsavedChanges()) {
            setShowConfirmDialog(true);
        } else {
            resetToSavedState();
            setModalOpen(false);
        }
    };

    const handleConfirmClose = () => {
        resetToSavedState();
        setShowConfirmDialog(false);
        setModalOpen(false);
    };

    const handleOnSubmitModelsProvision = () => {
        const provisionedModels = allCurrentAndFutureModelsSelected
            ? ['*']
            : rowSelectionModel.map((id) => rows.find((row) => row.id === id)?.objectName).filter(Boolean) as string[];

        savedAllCurrentAndFutureModelsSelected.current = allCurrentAndFutureModelsSelected;
        savedRowSelectionModel.current = [...rowSelectionModel];

        setModalOpen(false);
        onSubmitModelsProvision(provisionedModels);
    };

    return (
        <Box sx={{ minHeight: '15%' }}>
            <Typography variant="title">Selected Zuora Objects to receive Data</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography variant="body2" body='Billing Standard Objects' />
                <Button sx={{ marginBottom: '1%' }}
                    body='View Details'
                    dsOnClick={() => {
                        resetToSavedState();
                        setModalOpen(true);
                    }}
                    variant="text" />
            </Box>
            <Modal
                maxWidth="sm"
                fullWidth
                open={modalOpen}
                id="connector-select-models"
                dsOnClose={onClose}
                header={<ModalTitle dsOnClose={onClose}>Select Billing Standard Objects</ModalTitle>}
                body={
                    <DataGrid
                        disableRowSelectionOnClick
                        paginationModel={paginationModel}
                        isRowSelectable={() => !allCurrentAndFutureModelsSelected}
                        pageSizeOptions={[5, 10, 20]}
                        onPaginationModelChange={setPaginationModel}
                        sx={{
                            '.MuiDataGrid-toolbarContainer': {
                                display: 'block',
                            },
                            '.MuiTablePagination-selectLabel': {
                                margin: '0px'
                            },
                            '.MuiTablePagination-displayedRows': {
                                margin: '0px'
                            },
                            '.MuiDataGrid-filler': {
                                display: 'none'
                            }
                        }}
                        rows={rows}
                        disableColumnFilter
                        checkboxSelection
                        disableColumnSelector
                        disableDensitySelector
                        disableColumnMenu
                        slots={{
                            toolbar: GridToolbar,
                            noRowsOverlay: () => (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        height: "100%",
                                        padding: "1rem",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="gray"
                                        body="No Objects found for this view"
                                    />
                                </Box>
                            ),
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                csvOptions: { disableToolbarButton: true },
                                printOptions: { disableToolbarButton: true }
                            },
                            filterPanel: {
                                disableAddFilterButton: true
                            }
                        }}
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel);
                            if (!allCurrentAndFutureModelsSelected) {
                                setDestinationField('enabled_models', rows.filter((row) => newRowSelectionModel.includes(row.id)).map((row) => row['objectName']));
                            }
                        }}
                        rowSelectionModel={rowSelectionModel}
                        columns={[
                            {
                                field: 'id',
                                headerName: 'Number',
                            },
                            {
                                field: 'objectName',
                                headerName: 'Object Name',
                                sortable: true,
                                width: 200,
                            }
                        ]}
                    />
                }
                footer={
                    <Card id='select-future-objects' body={
                        <FormGroup>
                            <Checkbox
                                label={
                                    <Typography variant="title">All current and future objects will be synced</Typography>
                                }
                                dsOnChange={(e) => {
                                    setAllCurrentAndFutureModelsSelected(e.target.checked);
                                    if (e.target.checked) {
                                        const allRowIds = rows.map((row) => row.id);
                                        setRowSelectionModel(allRowIds);
                                        setDestinationField('enabled_models', ["*"]);
                                    } else {
                                        setRowSelectionModel([]);
                                        setDestinationField('enabled_models', []);
                                    }
                                }}
                                checked={allCurrentAndFutureModelsSelected}
                            />
                            <Typography variant='caption' color={designTokens.colors.coolGray300}>Objects added in the future will be synced automatically</Typography>
                            <Grid container direction="row" justify="flex-end">
                                <Grid item>
                                    <Button
                                        dsOnClick={handleOnSubmitModelsProvision}
                                        disabled={rowSelectionModel.length === 0 && !allCurrentAndFutureModelsSelected}
                                        body='Save'
                                        size="medium"
                                    />
                                </Grid>
                            </Grid>
                        </FormGroup>
                    }>
                    </Card>
                }
            />
            <DiscardChangesModal
                open={showConfirmDialog}
                onConfirm={handleConfirmClose}
                onClose={() => setShowConfirmDialog(false)}
            />
        </ Box >
    );
};